import {EventEmitter, Injectable} from '@angular/core';
import {IPlatformToolHandler} from '@shared/components/platform-tool/platform-tool-container/platform-tool-component';
import {InvalidateSubject} from '@shared/helpers/invalidate.subject';
import {MagicLinkModel, TabModel} from '@shared/models/platform-tool-models';
import {PlatformCommand} from '@shared/commands/command-base';
import {Subject} from 'rxjs';
import {CommandService} from '@core/services/command.service';
import {Router} from '@angular/router';
import {DialogService} from '@shared/services/dialog-service';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {SnackBarService} from '@core/services/snack-bar.service';
import {RootDynamicComponentHelper} from '@shared/helpers/root-dynamic-component-helper';
import {take, takeUntil} from 'rxjs/operators';
import {SetActivityFeedAction} from '@shared/components/platform-sidebar/store/platform-sidebar.action';
import {Store} from '@ngrx/store';
import {PlatformSidebarState} from '@shared/components/platform-sidebar/store/platform-sidebar.reducer';

@Injectable({
  providedIn: 'root',
})
export class GemsHistoryHandler implements IPlatformToolHandler {
  invalidateSubject: InvalidateSubject;
  magicLinkModel: MagicLinkModel;
  magicLinkModelValueChanged: EventEmitter<any>;
  ngUnsubscribe = new Subject<void>();
  selectedTabModel: TabModel;

  constructor(private commandService: CommandService,
              private router: Router,
              private dialogService: DialogService,
              private fuseSplashScreenService: FuseSplashScreenService,
              private snackBarService: SnackBarService,
              private platformSidebarStore: Store<PlatformSidebarState>) {
  }

  executeCommand(command: PlatformCommand) {
    console.log('[GemsHistoryHandler] onPlatformTool Handler Command', command);
    if (command.fieldValues['CommandHandler']) {
      const commandHandler = RootDynamicComponentHelper.getComponent(command.fieldValues['CommandHandler']);
      if (commandHandler) {
        commandHandler.executeCommand(command, this, null);
        return;
      }
    }
    switch (command.name) {
      default:
        this.executeCommandToApi(command);
        break;
    }
  }

  valueChanged($event) {
    console.log('Inside Handler', $event);
    if (this.handleAction($event)) {
      return;
    }
    if (this.handleCommand($event)) {
      return;
    }
    if (this.handleByFieldDefinition($event)) {
      return;
    }
  }

  private handleByFieldDefinition($event): boolean {
    const command = this.magicLinkModel.additionalCommands[$event.fieldDefinition.key];
    if (command) {
      command.fieldValues['ItemId'] = $event.item.id;
      if ($event.item.cells['FileRelativePath'] && $event.item.cells['FileRelativePath'].value) {
        command.fieldValues['FileRelativePath'] = $event.item.cells['FileRelativePath'].value;
      }
      if ($event.item.cells['FlowExecutionId'] && $event.item.cells['FlowExecutionId'].value ) {
        command.fieldValues['FlowExecutionId'] = $event.item.cells['FlowExecutionId'].value;
      }
      this.executeCommand(command);
      return true;
    }
    return false;
  }

  private executeCommandToApi(command: PlatformCommand) {
    if (command.api && command.api.length > 0 && !command.isDisabled) {
      this.fuseSplashScreenService.show();
      this.commandService.executePlatformCommand(command).pipe(take(1)).subscribe(commandResponse => {
          console.log('[GemsHistoryHandler] Command Response', commandResponse);
          this.fuseSplashScreenService.hide();
          if (commandResponse.fieldValues['RedirectUrl']) {
            this.router.navigateByUrl(commandResponse.fieldValues['RedirectUrl']);
          }
          if (commandResponse.fieldValues['SnackBarMessage']) {
            const duration = commandResponse.fieldValues['SnackBarMessage'].length * 80;
            this.snackBarService.showInformation(commandResponse.fieldValues['SnackBarMessage'], duration);
          }
          if (commandResponse.fieldValues['ErrorMessage']) {
            this.snackBarService.showError(commandResponse.fieldValues['SnackBarMessage']);
          }

          this.invalidateSubject.invalidate();
        },
        error => {
          console.log(error);
          this.fuseSplashScreenService.hide();
        });
    }
  }

  private handleCommand($event): boolean {
    if ($event.command) {
      switch ($event.command.name) {
        case 'CommandName':
          return true;
        default:
          this.executeCommand($event.command);
          return true;
      }
    }
    return false;
  }

  private handleAction($event): boolean {
    if (this.selectedTabModel && $event.action) {
      const item = $event.item;
      const command = this.magicLinkModel.additionalCommands[$event.action.id];
      if (command) {
        command.fieldValues['Item'] = item.id;
        if ($event.item.cells['FileRelativePath'] && $event.item.cells['FileRelativePath'].value) {
          command.fieldValues['FileRelativePath'] = $event.item.cells['FileRelativePath'].value;
        }
        this.executeCommand(command);
        return true;
      }
      switch ($event.action.id) {
        case 'ViewActivities':
          this.viewActivities(item);
          return true;
        case 'Edit':
          this.openEditor($event);
          return true;
      }
    }
    return false;
  }

  setSelectedTab(selectedTab: TabModel): void {
    this.selectedTabModel = selectedTab;
  }

  private viewActivities(item: any) {
    const referenceCode = item.id;
    const title = `(${item.cells['ReturnName'].value})`;
    const activityInfo = {type: 'ReferenceCode', 'id': referenceCode, 'title': title, isDefault: false};
    this.platformSidebarStore.dispatch(new SetActivityFeedAction(activityInfo));
  }
  private openEditor($event) {
    this.dialogService.showCellEditorDialog($event.item, $event.fieldDefinition).pipe(takeUntil(this.ngUnsubscribe)).subscribe(state => {
      console.log('After close state: ', state);
      if (state.action === 'OK') {
        const item = {id: $event.item.id, fieldValues: {}};
        const value = $event.item.cells[$event.fieldDefinition.key].value;
        item.fieldValues[$event.fieldDefinition.key] = value;
        const command = new PlatformCommand('UpdateGemsEntityFieldCommand', 'FileStoreApi');
        command.fieldValues['ItemId'] = $event.item.id;
        command.fieldValues['FieldKey'] = $event.fieldDefinition.key;
        command.fieldValues['Value'] = value;
        this.executeCommand(command);
      }
    });
  }
  destroy(): void {
    this.invalidateSubject.close();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
